import { CompanyUserType } from 'lib/api_ts/companiesApi';
import { DeliverableType, MilestoneType, SubtaskType, TaskType, StreamType } from 'lib/api_ts/overviewApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gantt as WxGantt, Willow } from 'wx-react-gantt';
//import "wx-react-gantt/dist/gantt.css"; //conflicts with own tooltip styling
import { format, isValid } from 'date-fns';
import { it } from 'date-fns/locale';
import { Form, Tab, Tabs } from 'react-bootstrap';
import Legend from './Gantt/Legend';
import DetailPopover from './Gantt/DetailPopover';

import { OETrans } from 'components/translation/OETrans';
import { TaskStatusMapType } from './Overview';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { ColDef, ColumnApi, GridApi } from 'ag-grid-community';
import TodayPopover from './Gantt/TodayPopover';
import DateInputs from './Gantt/DateInputs';

type GanttTaskType = {
	id: string;
	start: string;
	end?: string;
	text: string;
	progress: number;
	parent?: string;
	type: 'summary' | 'task' | 'milestone';
	open?: boolean;
	details?: string;
	responsible?: string;
};

export type RawTaskType = MilestoneType | DeliverableType | TaskType | StreamType | SubtaskType;

interface GanttProps {
	activeKey: string;
	milestones: MilestoneType[];
	deliverables: DeliverableType[];
	tasks: TaskType[];
	users: CompanyUserType[];
	streams: StreamType[];
	taskStatusMap: TaskStatusMapType;
}

const Gantt = ({ activeKey, milestones, deliverables, tasks, users, streams, taskStatusMap }: GanttProps) => {
	const DAY_CELL_WIDTH = 40;
	const MONTH_CELL_WIDTH = 300;
	const INITIAL_TABLE_WIDTH = 349;
	const HEADER_CELL_HEIGHT = 36;
	const DAY_HEADER_HEIGHT = 108; // N.D.R. 3 rows of 36px each
	const MONTH_HEADER_HEIGHT = 72; // N.D.R. 2 rows of 36px each
	const ROW_HEIGHT = 120;
	const DOT_SIZE = 16;
	const containerRef = useRef<HTMLDivElement>(null);
	const ganttContainerRef = useRef<HTMLDivElement>(null);
	const ganttApiRef = useRef<unknown>(null);
	const gridRef = useRef<AgGridReact>(null);

	const [isLoading, setIsLoading] = useState(true);

	const [start, setStart] = useState('');
	const [defaultStart, setDefaultStart] = useState('');
	const [end, setEnd] = useState('');
	const [defaultEnd, setDefaultEnd] = useState('');
	const [cellWidth, setCellWidth] = useState(DAY_CELL_WIDTH);
	const dateInputs = [
		{
			name: 'start',
			label: 'Seleziona la data di inizio',
			value: start,
			default: defaultStart,
			set: setStart,
		},
		{
			name: 'end',
			label: 'Seleziona la data di fine',
			value: end,
			default: defaultEnd,
			set: setEnd,
		},
	];

	const dayStyle = (a: Date) => {
		const day = a.getDay() === 6 || a.getDay() === 0;
		return day ? 'sday' : '';
	};

	const allScales = useMemo(
		() => [
			[
				{
					unit: 'month',
					step: 1,
					format: (date: Date) => format(date, 'MMMM yyyy', { locale: it }),
					css: 'month-year-scale',
				},
				{
					unit: 'day',
					step: 1,
					format: (date: Date) => format(date, 'd', { locale: it }),
					css: (date: Date) => `fw-bold  ${dayStyle(date)}`.trim(),
				},
				{
					unit: 'day',
					step: 1,
					format: (date: Date) => format(date, 'EEEEE', { locale: it }),
					css: dayStyle,
				},
			],
			[
				{
					unit: 'year',
					step: 1,
					format: (date: Date) => format(date, 'yyyy', { locale: it }),
					css: 'year-scale',
				},
				{
					unit: 'month',
					step: 1,
					format: (date: Date) => format(date, 'MMMM', { locale: it }),
				},
			],
		],
		[],
	);

	const [scales, setScales] = useState(allScales[0]);

	const zoomLevels = useMemo(
		() => [
			{
				value: 'daily',
				label: 'Giorno',
			},
			{
				value: 'monthly',
				label: 'Mese',
			},
		],
		[],
	);
	const [selectedZoomLevel, setSelectedZoomLevel] = useState(zoomLevels[0]);

	const [diffDays, setDiffDays] = useState(0);

	useEffect(() => {
		if (!start || !end) return;
		const startDate = new Date(start);
		const endDate = new Date(end);
		setDiffDays((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
	}, [start, end]);

	const partnersUsers = useMemo(
		() => [
			...streams.flatMap((stream) => stream.responsibles.flatMap((responsible) => responsible.users)),
			...streams.flatMap((stream) => stream.tasks.flatMap((task) => task.responsibles.flatMap((responsible) => responsible.users))),
		],
		[streams],
	);

	const possibleResponsibles = useMemo(() => [...users, ...partnersUsers], [users, partnersUsers]);

	const rawStreams = streams.filter((stream) => stream.startDate || stream.endDate);

	const rawTasks = tasks;
	const rawSubtasks = tasks.flatMap((task) => task.subtasks);
	const rawMilestones = milestones.filter((milestone) => milestone.effectiveDate || milestone.forecastDate);
	const rawDeliverables = deliverables.filter((deliverable) => deliverable.effectiveDate || deliverable.forecastDate);

	const formattedWps: GanttTaskType[] = useMemo(
		() =>
			rawStreams.map((stream) => {
				const responsible = stream.responsibles.length > 0 ? stream.responsibles[0].name : '';

				const fixedEndDate = new Date(stream.endDate);
				fixedEndDate.setDate(fixedEndDate.getDate() + 1);

				return {
					id: `wp-${stream.id}`,
					start: stream.startDate,
					end: fixedEndDate.toISOString(),
					text: stream.name,
					// text: `WP ${stream.id}`,
					responsible,
					progress: 0,
					type: 'summary',
					open: stream.tasks.length > 0,
				};
			}),
		[rawStreams],
	);

	const formattedTasks: GanttTaskType[] = useMemo(
		() =>
			rawStreams.flatMap((stream) =>
				stream.tasks.map((task) => {
					const responsibleUser = possibleResponsibles.find((possibleResponsible) => possibleResponsible.email === task.owner);
					const responsible = responsibleUser
						? `${responsibleUser.firstName} ${responsibleUser.lastName}`
						: task.responsibles.length > 0
							? task.responsibles[0].name
							: '';

					const fixedEndDate = new Date(task.endDate);
					fixedEndDate.setDate(fixedEndDate.getDate() + 1);
					return {
						id: `task-${task.id}`,
						start: task.startDate,
						end: fixedEndDate.toISOString(),
						text: task.name,
						// text: `Task ${task.id}`,
						responsible,
						progress: 0,
						parent: `wp-${stream.id}`,
						type: 'task',
						open: tasks.find((taskFromAPi) => taskFromAPi.id === task.id)?.subtasks.length > 0,
					};
				}),
			),
		[rawStreams, possibleResponsibles, tasks],
	);

	const formattedSubtasks: GanttTaskType[] = useMemo(
		() =>
			rawTasks.flatMap((task) =>
				task.subtasks.map((subtask) => {
					const start = subtask.endDate ? subtask.endDate : subtask.startDate;
					const responsibleUser = possibleResponsibles.find((possibleResponsible) => possibleResponsible.email === subtask.owner);
					const responsible = responsibleUser
						? `${responsibleUser.firstName} ${responsibleUser.lastName}`
						: subtask.responsibles.length > 0
							? subtask.responsibles[0].name
							: '';
					return {
						id: `subtask-${subtask.id}`,
						start,
						text: subtask.name,
						// text: `Subtask ${subtask.id}`,
						responsible,
						progress: 0,
						parent: `task-${task.id}`,
						type: 'milestone',
					};
				}),
			),
		[rawTasks, possibleResponsibles],
	);

	const formatMilestonesOrDeliverables = useCallback(
		(tasks: (MilestoneType | DeliverableType)[], type: 'milestone' | 'deliverable'): GanttTaskType[] => {
			return tasks.map((task) => {
				const start = task.effectiveDate || task.forecastDate;
				const responsibleUser = possibleResponsibles.find((possibleResponsible) => possibleResponsible.id === task.responsible);
				const responsible = responsibleUser ? `${responsibleUser.firstName} ${responsibleUser.lastName}` : '';
				return {
					id: `${type}-${task.id}`,
					start,
					text: task.title,
					// text: `${type.charAt(0).toUpperCase() + type.slice(1)} ${task.id}`,
					responsible,
					progress: 0,
					parent: `wp-${task.wpStreamId}`,
					type: 'milestone',
				};
			});
		},
		[possibleResponsibles],
	);

	const formattedMilestones = useMemo(
		() => formatMilestonesOrDeliverables(rawMilestones, 'milestone'),
		[rawMilestones, formatMilestonesOrDeliverables],
	);
	const formattedDeliverables = useMemo(
		() => formatMilestonesOrDeliverables(rawDeliverables, 'deliverable'),
		[rawDeliverables, formatMilestonesOrDeliverables],
	);

	useEffect(() => {
		if (!containerRef.current) return;
		const updateWidth = () => {
			const parentWidth = containerRef.current.parentElement?.clientWidth;
			containerRef.current.style.width = `${parentWidth}px`;
		};
		updateWidth();
		window.addEventListener('resize', updateWidth);
		return () => window.removeEventListener('resize', updateWidth);
	}, [activeKey]);

	const allRawTasks = useMemo(
		() => ({
			wp: rawStreams,
			task: rawTasks,
			subtask: rawSubtasks,
			milestone: rawMilestones,
			deliverable: rawDeliverables,
		}),
		[rawStreams, rawTasks, rawSubtasks, rawMilestones, rawDeliverables],
	);

	const allFormattedTasks = useMemo(
		() => [...formattedWps, ...formattedTasks, ...formattedSubtasks, ...formattedMilestones, ...formattedDeliverables],
		[formattedWps, formattedTasks, formattedSubtasks, formattedMilestones, formattedDeliverables],
	);

	useEffect(() => {
		if (rawStreams.length === 0) return;

		if (start && end) return;

		// Trova la data di inizio più vecchia
		const earliestStart = rawStreams
			.map((stream) => stream.startDate)
			.filter((date): date is string => Boolean(date)) // Rimuove `null` o `undefined`
			.map((date) => new Date(date)) // Converte in Date
			.reduce((min, date) => (date < min ? date : min), new Date());

		// Imposta `start` a un giorno prima della più vecchia
		earliestStart.setDate(earliestStart.getDate() - 1);
		setStart(format(earliestStart, 'yyyy-MM-dd', { locale: it }));
		setDefaultStart(format(earliestStart, 'yyyy-MM-dd', { locale: it }));

		// Trova la data di fine più recente
		const latestEnd = rawStreams
			.map((stream) => stream.endDate)
			.filter((date): date is string => Boolean(date)) // Rimuove null o undefined
			.map((date) => new Date(date)) // Converte in Date
			.reduce((max, date) => (date > max ? date : max), new Date()); // Trova la data più recente

		// Imposta la data di fine (end) come un giorno dopo la più recente
		latestEnd.setDate(latestEnd.getDate() + 1);

		setEnd(format(latestEnd, 'yyyy-MM-dd', { locale: it }));
		setDefaultEnd(format(latestEnd, 'yyyy-MM-dd', { locale: it }));
	}, [rawStreams, start, end /*, diffDays */]);

	const cellRenderer = useCallback((props: { data: GanttTaskType; columnApi: ColumnApi; colDef: { field: string } }) => {
		const title = props.data.text;
		const taskType = props.data.id.split('-')[0];
		const formattedTaskType = taskType === 'wp' ? 'Work Package' : taskType.charAt(0).toUpperCase() + taskType.slice(1);
		const responsible = props.data.responsible;
		return (
			<div
				id={`cellLabel-${props.data.id}`}
				className={`wx-cellLabel wx-${taskType}-cellLabel`}
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					gap: '0.5rem',
					height: '100%',
					cursor: 'pointer',
					position: 'relative',
				}}
			>
				<div>
					<div
						style={{
							fontSize: '16px',
							fontWeight: 700,
							lineHeight: '24px',
							letterSpacing: 0,
							textOverflow: 'ellipsis',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							textDecoration: 'underline',
						}}
					>
						{title}
					</div>
					<div
						style={{
							fontSize: '14px',
							lineHeight: '24px',
							textOverflow: 'ellipsis',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
						}}
					>
						{formattedTaskType}
					</div>
				</div>
				<div>
					<div
						style={{
							fontSize: '16px',
							lineHeight: '24px',
							fontWeight: 700,
							letterSpacing: '0%',
							textOverflow: 'ellipsis',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
						}}
					>
						{responsible}
					</div>
					<div
						style={{
							fontSize: '14px',
							lineHeight: '24px',
							textOverflow: 'ellipsis',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
						}}
					>
						Responsabile
					</div>
				</div>

				<div
					style={{
						position: 'absolute',
						top: 0,
						// right: '-16px',
						right: 0,
						bottom: 0,
						width: '24px',
						cursor: 'ew-resize',
					}}
					onMouseDown={(event) => {
						const colId = props.colDef.field;
						const startX = event.clientX;
						const startWidth = props.columnApi.getColumn(colId)?.getActualWidth() ?? INITIAL_TABLE_WIDTH;

						const onMouseMove = (event: MouseEvent) => {
							const diff = event.clientX - startX;
							const newWidth = Math.max(50, startWidth + diff);
							props.columnApi.setColumnWidth(colId, newWidth, false);
						};

						const onMouseUp = () => {
							document.removeEventListener('mousemove', onMouseMove);
							document.removeEventListener('mouseup', onMouseUp);
						};

						document.addEventListener('mousemove', onMouseMove);
						document.addEventListener('mouseup', onMouseUp);
					}}
				/>
			</div>
		);
	}, []);

	const columnDefs = useMemo(() => {
		return [
			{
				field: 'id',
				headerName: 'Attività',
				width: INITIAL_TABLE_WIDTH,
				maxWidth: INITIAL_TABLE_WIDTH * 3,
				cellRenderer,
			},
		];
	}, [cellRenderer, INITIAL_TABLE_WIDTH]);

	const defaultColDef: ColDef = useMemo(
		() => ({
			resizable: true,
			// checkboxSelection: true,
			suppressMovable: true,
			// cellStyle: { padding: '3px 1rem' },
		}),
		[],
	);

	const changeZoomScale = useCallback(
		(zoomToSelect: 'daily' | 'monthly') => {
			setSelectedZoomLevel(zoomLevels.find((zoomLevel) => zoomLevel.value === zoomToSelect));
			switch (zoomToSelect) {
				case 'daily':
					setScales(allScales[0]);
					setCellWidth(DAY_CELL_WIDTH);
					const latestEnd = rawStreams
						.map((stream) => stream.endDate)
						.filter((date): date is string => Boolean(date)) // Rimuove null o undefined
						.map((date) => new Date(date)) // Converte in Date
						.reduce((max, date) => (date > max ? date : max), new Date()); // Trova la data più recente

					// Imposta la data di fine (end) come un giorno dopo la più recente
					latestEnd.setDate(latestEnd.getDate() + 1);
					setEnd(format(latestEnd, 'yyyy-MM-dd', { locale: it }));
					setDefaultEnd(format(latestEnd, 'yyyy-MM-dd', { locale: it }));

					break;
				case 'monthly':
					setScales(allScales[1]);
					setCellWidth(MONTH_CELL_WIDTH);
					const currentState =
						ganttApiRef.current && typeof ganttApiRef.current === 'object' && 'getState' in ganttApiRef.current
							? (ganttApiRef.current as { getState: () => { end: string } }).getState()
							: null;
					if (!currentState && !currentState.end) return;
					const currentEnd = currentState.end;
					const currentEndDate = new Date(currentEnd);

					if (!currentEndDate) return;
					const endDate = new Date(currentEndDate.getFullYear(), currentEndDate.getMonth() + 1, 1);

					if (!isValid(endDate)) return;
					setEnd(format(endDate, 'yyyy-MM-dd', { locale: it }));
					setDefaultEnd(format(endDate, 'yyyy-MM-dd', { locale: it }));

					break;
			}
			document.documentElement.style.setProperty('--table-width', `${INITIAL_TABLE_WIDTH}px`);
		},
		[allScales, rawStreams, zoomLevels],
	);

	// N.D.R. Centers no-range elements if zoom scale is daily
	// and removes the class if zoom scale is monthly
	useEffect(() => {
		const noRangeElements = document.querySelectorAll('.wx-milestone.x2-1qryx5p .wx-content.x2-1qryx5p');
		switch (selectedZoomLevel.value) {
			case 'daily':
				noRangeElements.forEach((element) => {
					element.classList.add('daily');
				});
				break;
			case 'monthly':
				noRangeElements.forEach((element) => {
					element.classList.remove('daily');
				});
				break;
		}
	}, [selectedZoomLevel]);

	// N.D.R. This effect is used to hide the first cell text
	// if the width is too narrow
	useEffect(() => {
		const frame = requestAnimationFrame(() => {
			const firstCell = ganttContainerRef.current?.querySelector('.wx-cell:first-of-type') as HTMLElement | null;
			if (!firstCell) return;

			const { width } = firstCell.getBoundingClientRect();
			// N.D.R. the longest possible text in first cell is
			// "Settembre YYYY" and fits in 4 DAY_CELL_WIDTH
			const isTooNarrow = width < DAY_CELL_WIDTH * 4;
			firstCell.style.textIndent = isTooNarrow ? '-9999px' : '0';
		});

		return () => cancelAnimationFrame(frame);
	}, [selectedZoomLevel.value, start]);

	// N.D.R. This effect is used to set the offset
	// of the today marker in the monthly view
	useEffect(() => {
		if (selectedZoomLevel.value === 'daily') return;
		const today = new Date();
		const currentDay = today.getDate(); // giorno del mese (1-31)
		const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate(); // numero di giorni nel mese

		const proportion = (currentDay - 1) / (daysInMonth - 1); // da 0 a 1
		const offset = proportion * 300;

		document.documentElement.style.setProperty('--monthly-today-offset', `${offset}px`);
	}, [selectedZoomLevel.value]);

	const isFirstRender = useRef(true);

	useEffect(() => {
		if (!diffDays || !isFirstRender.current) return;
		if (diffDays > 90) {
			changeZoomScale('monthly');
		}
		isFirstRender.current = false;
		setIsLoading(false);
	}, [diffDays, changeZoomScale]);

	const rowData = useMemo(() => {
		const tempRowData: GanttTaskType[] = [];
		formattedWps.forEach((wp) => {
			tempRowData.push(wp);
			formattedTasks.forEach((task) => {
				if (task.parent === wp.id) {
					tempRowData.push(task);
					formattedSubtasks.forEach((subtask) => {
						if (subtask.parent === task.id) {
							tempRowData.push(subtask);
						}
					});
				}
			});
			formattedMilestones.forEach((milestone) => {
				if (milestone.parent === wp.id) {
					tempRowData.push(milestone);
				}
			});
			formattedDeliverables.forEach((deliverable) => {
				if (deliverable.parent === wp.id) {
					tempRowData.push(deliverable);
				}
			});
		});
		return tempRowData;
	}, [formattedWps, formattedTasks, formattedSubtasks, formattedMilestones, formattedDeliverables]);

	// N.D.R. uncomment if the height is explicit for ganttContainerRef

	// useEffect(() => {
	// 	if (!ganttContainerRef.current) return;
	// 	const scrollingElement = ganttContainerRef.current.children[0].children[0].children[0].children[0].children[1] as HTMLElement;
	// 	const handleScroll = (event: Event) => {
	// 		const tableContainer = document.querySelector('.ag-body-viewport') as HTMLElement;
	// 		if (!tableContainer) return;
	// 		tableContainer.scrollTop = (event.target as HTMLElement).scrollTop;
	// 	};
	// 	scrollingElement.addEventListener('scroll', handleScroll);
	// 	return () => {
	// 		scrollingElement.removeEventListener('scroll', handleScroll);
	// 	};
	// }, []);

	const handleGridReady = useCallback((params: { api: GridApi }) => {
		params.api.addEventListener('columnResized', () => {
			if (!params.api) return;
			const headerCell = containerRef.current.querySelector('.ag-header-cell') as HTMLElement;
			if (!headerCell) return;

			const newWidth = headerCell.getBoundingClientRect().width;
			document.documentElement.style.setProperty('--table-width', `${newWidth}px`);
		});
	}, []);
	const handleSelectionChanged = useCallback((params: { api: GridApi }) => {
		const selectedRows: GanttTaskType[] = params.api.getSelectedRows();
		if (selectedRows.length === 0) return;

		const selectedTask = selectedRows[0];

		const selectedLegendButton = document.querySelector(`#wx-legend-button-${selectedTask.id.split('-')[0]}`) as HTMLButtonElement | null;
		if (selectedLegendButton && selectedLegendButton.classList.contains('hiding')) {
			selectedLegendButton.click();
		}
		// N.D.R. type guard to safely handle ganttApiRef.current = unknown
		if (
			!ganttApiRef.current ||
			typeof ganttApiRef.current !== 'object' ||
			!('exec' in ganttApiRef.current) ||
			typeof ganttApiRef.current.exec !== 'function'
		) {
			return;
		}

		ganttApiRef.current.exec('select-task', { id: selectedTask.id });
	}, []);

	useEffect(() => {
		if (!containerRef.current) return;
		const headerCell = containerRef.current?.querySelector('.ag-header-cell') as HTMLElement;
		if (!headerCell) return;

		const observer = new ResizeObserver(() => {
			requestAnimationFrame(() => {
				const actualWidth = headerCell.getBoundingClientRect().width;
				const cssWidth = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--table-width'));

				if (Math.abs(cssWidth - actualWidth) > 1) {
					document.documentElement.style.setProperty('--table-width', `${actualWidth}px`);
				}
			});
		});

		observer.observe(headerCell);

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<div
			ref={containerRef}
			style={{
				height: 'auto',
				width: 0,
				margin: 'auto',
				marginTop: '50px',
				position: 'relative',
			}}
		>
			{isLoading ? (
				<div
					style={{
						position: 'absolute',
						inset: '0 0 0 -36px',
						backgroundColor: '#fff',
						zIndex: 1000,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<div className="spinner-border" role="status">
						<span className="visually-hidden">
							<OETrans type="propTrans" code="common.loading" fallback={'Caricamento'} />
							...
						</span>
					</div>
				</div>
			) : null}
			{/* <div className="d-flex flex-column flex-xxl-row justify-content-xxl-between mb-5 gap-3"> */}
			<div className="d-flex justify-content-between mb-5 gap-5">
				<Form style={{ display: 'flex', gap: '1rem' }}>
					<div>
						<div className="form-label" style={{ marginBottom: '8px' }}>
							Vista
						</div>
						<Tabs
							variant="pills"
							onSelect={(eventKey) => changeZoomScale(eventKey as 'daily' | 'monthly')}
							activeKey={selectedZoomLevel.value}
							className="zoom-pills mb-3 px-1"
							style={{ flexWrap: 'nowrap' }}
						>
							{zoomLevels.map((zoomLevel) => (
								<Tab
									key={zoomLevel.value}
									eventKey={zoomLevel.value}
									title={zoomLevel.label}
									active={selectedZoomLevel.value === zoomLevel.value}
								/>
							))}
						</Tabs>
					</div>
					<DateInputs dateInputs={dateInputs} start={start} end={end} />
				</Form>
				<div
					// className="d-none d-xxl-block"
					style={{ height: '100px', width: '1px', minWidth: '1px', backgroundColor: '#C1C1C1' }}
				/>
				<Legend gridRef={gridRef} />
			</div>

			<DetailPopover
				possibleResponsibles={possibleResponsibles}
				taskStatusMap={taskStatusMap}
				activeKey={activeKey}
				ganttApiRef={ganttApiRef}
				gridRef={gridRef}
				allRawTasks={allRawTasks}
				selectedZoomLevel={selectedZoomLevel.value}
				ganttContainerRef={ganttContainerRef}
			/>

			<div style={{ position: 'relative' }}>
				<div
					className="vertical-line"
					style={{
						position: 'absolute',
						top: selectedZoomLevel.value === 'daily' ? DAY_HEADER_HEIGHT : MONTH_HEADER_HEIGHT,
						bottom: '47px',
						left: '-24px',
						width: '2px',
						backgroundColor: '#4A5968',
						zIndex: 13,
					}}
				/>
				{rowData.map((row, index) => {
					const isWp = row.id.startsWith('wp-');

					if (!isWp) return null;
					const headerTop = selectedZoomLevel.value === 'daily' ? DAY_HEADER_HEIGHT : MONTH_HEADER_HEIGHT;
					const offset = index * ROW_HEIGHT;
					const top = headerTop + offset;
					return (
						<div
							className="vertical-line"
							key={row.id}
							style={{
								position: 'absolute',
								top,
								height: `${ROW_HEIGHT / 2}px`,
								left: '-25px',
								width: '4px',
								backgroundColor: '#fff',
								zIndex: 15,
							}}
						>
							<div
								style={{
									width: `${DOT_SIZE}px`,
									height: `${DOT_SIZE}px`,
									backgroundColor: '#4A5968',
									borderRadius: '50%',
									position: 'absolute',
									top: `${HEADER_CELL_HEIGHT + DOT_SIZE / 2}px`,
									left: '-6px',
								}}
							/>
						</div>
					);
				})}
				{rowData.map((row, index) => {
					const isBeforeWp = rowData[index + 1]?.id.startsWith('wp-');

					const isNotLast = index < rowData.length - 1;
					if (!isBeforeWp && isNotLast) return null;
					const headerTop = selectedZoomLevel.value === 'daily' ? DAY_HEADER_HEIGHT : MONTH_HEADER_HEIGHT;
					const offset = index * ROW_HEIGHT + ROW_HEIGHT / 2; // N.D.R. add half row height to center the dot
					const top = headerTop + offset;
					return (
						<div
							className="vertical-line"
							key={row.id}
							style={{
								position: 'absolute',
								top,
								height: `${ROW_HEIGHT / 2}px`,
								left: '-25px',
								width: '4px',
								backgroundColor: '#fff',
								zIndex: 15,
							}}
						>
							<div
								style={{
									width: `${DOT_SIZE}px`,
									height: `${DOT_SIZE}px`,
									backgroundColor: '#4A5968',
									borderRadius: '50%',
									position: 'absolute',
									top: `-${DOT_SIZE}px`,
									left: '-6px',
								}}
							/>
						</div>
					);
				})}

				<Willow>
					<div className="gantt-container" style={{ position: 'relative', fontFamily: 'Barlow, sans-serif', overflow: 'hidden' }}>
						<div
							className="ag-theme-alpine"
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								bottom: 0,
								width: 'var(--table-width)',
								zIndex: 3,
								boxShadow: '33px 3px 25.9px -8px rgba(0,0,0,0.1)',
								borderRight: '4px solid #C1C1C1',
								boxSizing: 'content-box',
							}}
						>
							<AgGridReact
								// N.D.R. key is used to force a re-render
								// when the zoom level changes
								ref={gridRef}
								key={selectedZoomLevel.value}
								rowData={rowData}
								columnDefs={columnDefs}
								onGridReady={handleGridReady}
								rowSelection="single"
								onSelectionChanged={handleSelectionChanged}
								defaultColDef={defaultColDef}
								rowHeight={ROW_HEIGHT}
								headerHeight={selectedZoomLevel.value === 'daily' ? 108 : 72}
							/>
						</div>
						<div
							ref={ganttContainerRef}
							style={{
								translate: 'var(--table-width)',
								width: 'calc(100% - var(--table-width))',
							}}
						>
							<WxGantt
								init={(api: unknown) => {
									ganttApiRef.current = api;
								}}
								tasks={allFormattedTasks}
								columns={[]}
								scales={scales}
								readonly={true}
								cellWidth={cellWidth}
								start={start}
								end={end}
								markers={[
									{
										start: new Date(),
										text: '',
										css: `wx-default today_popover today_popover-${selectedZoomLevel.value}`,
									},
								]}
								cellHeight={ROW_HEIGHT}
							/>
							<hr style={{ marginTop: '16px' }} />
						</div>
					</div>
					<TodayPopover ganttContainerRef={ganttContainerRef} />
				</Willow>
				<div id="patch" />
			</div>
		</div>
	);
};

export default Gantt;
